'use client'
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { buildFaqSchema, stringifyJsonLd } from "@/utils/faqSchema";

const faqDataHHM = [
  {
    id: "01",
    question: "Are there any transport facilities available for the process members?",
    answer: "For the teaching and non teaching staff, the University provides transportation facilities in form of cars, buses and couches which provide transportation to and from the campus. The bus routes are strategically outlined in such a way that allows the highest form of convenience to the staff members in areas within Vadodara, along with cities surrounding Vadodara.",
  }
];
// --- END: DUMMY FAQ DATA ---

// This component now accepts only the title and subtitle as props,       
// using the internal faqDataHHM array.
export default function FAQCareer(
  { 
    title = "Frequently Asked Questions", 
    subtitle = "Find quick answers to the most common questions about our programs." 
  }
) 
  {
  const [openItem, setOpenItem] = useState("01");
  const [windowWidth, setWindowWidth] = useState(0);
  const faqSchema = buildFaqSchema(faqDataHHM);

  // Set window width only in the client-side
  useEffect(() => {
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
    }

    const handleResize = () => {
      if (typeof window !== "undefined") {
        setWindowWidth(window.innerWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? "" : id);
  };

  return (
    <section className="w-full bg-white max-w-[var(--max-page-width)] px-[var(--container-padding)] mx-auto my-[50px] md:my-[80px] lg:my-[100px]">
          {faqSchema && (
            <script
              id="FAQSchema"
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: stringifyJsonLd(faqSchema) }}
            />
          )}
          {/* Header */}
          <div className="container mx-auto text-left mb-[12px] md:mb-[20px]">
            <h2 className="mb-[12px] tracking-[-1.2px] text-centre section-title ">{title}</h2>
            <p className="text-[black] leading-[23px] text-centre mb-[30px] md:mb-[48px]">
              {subtitle}
            </p>
          </div>
          
          {/* FAQ Items */}
          <div className="">
            {faqDataHHM.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div
                  className={` border transition-all duration-500 ease-out mb-[12px]
                    ${openItem === item.id
                      ? "bg-white border-blue-200/50 shadow-2xl shadow-blue-500/10 scale-[1.01] xs:scale-[1.02] ring-1 ring-blue-100/50"
                      : "bg-[#F9F9FD] border-gray-200/40 shadow-lg shadow-gray-500/5 hover:bg-white hover:border-blue-200/30 hover:shadow-xl hover:shadow-blue-500/8 hover:scale-[1.005] xs:hover:scale-[1.01] hover:ring-1 hover:ring-blue-100/30"
                    }
                  `}
                >
                  {/* Question Row */}
                  <div
                    className={`flex items-center justify-between px-[14px] py-[20px] md:px-[36px] md:py-[30px] cursor-pointer group`}
                    onClick={() => toggleItem(item.id)}
                  >
                    <div className="flex items-center flex-grow min-w-0 mr-2 xs:mr-3 sm:mr-4">
                      {/* Number */}
                      <div
                        className={`
                          hidden md:flex transition-all duration-300 ease-in-out flex-shrink-0 mr-[30px] text-[24px] text-[#979CB1]
                        `}
                      >
                        {item.id}
                      </div>

                      {/* Question */}
                      <p
                        className="transition-all duration-300 ease-in-out text-black text-[20px] py-[16px]"
                        style={{ fontFamily:"saans-medium"}}>
                        {item.question}
                      </p>
                    </div>

                    {/* Toggle Button */}
                    <motion.button
                      className={`
                        w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 
                        rounded-full border-none transition-all duration-300 ease-in-out
                        flex items-center justify-center flex-shrink-0
                        ${openItem === item.id
                          ? "bg-[#0475B3] text-white shadow-lg shadow-blue-600/25 ring-1 xs:ring-2 ring-blue-200/50"
                          : "bg-white text-slate-400 shadow-md shadow-gray-500/10 hover:bg-[#0475B3] hover:text-white hover:shadow-lg hover:shadow-[#0475B3] hover:ring-1 xs:hover:ring-2 hover:ring-[#0475B3] ring-1 ring-gray-200/30"
                        }
                      `}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <AnimatePresence mode="wait">
                        {openItem === item.id ? (
                          <motion.div
                            key="minus"
                            initial={{ opacity: 0, rotate: -90 }}
                            animate={{ opacity: 1, rotate: 0 }}
                            exit={{ opacity: 0, rotate: 90 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Minus size={windowWidth < 640 ? 14 : 18} strokeWidth={2.5} />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="plus"
                            initial={{ opacity: 0, rotate: 90 }}
                            animate={{ opacity: 1, rotate: 0 }}
                            exit={{ opacity: 0, rotate: -90 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Plus size={windowWidth < 640 ? 14 : 18} strokeWidth={2.5} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>
                  </div>

                  {/* Answer - Animated */}
                  <AnimatePresence>
                    {openItem === item.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="">
                          <div 
                            className=""
                          >
                            <motion.p
                              className="mb-[30px] text-[#3D677E] text-[14px] leading-[20px]  px-[14px] md:px-[88px] "
                              initial={{ y: -10, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ duration: 0.3, delay: 0.1 }}
                            >
                              {item.answer}
                            </motion.p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
    </section>
  );
}
